import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Copy, ChevronDown, ChevronUp, FileCheck2, Loader2 } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

function Pair({ p, onDone }) {
    const [keep, setKeep] = useState(p.keep_idx);
    const [busy, setBusy] = useState(false);
    const remove = p.entries.find((e) => e.idx !== keep).idx;
    const kp = p.entries.find((e) => e.idx === keep);
    const rm = p.entries.find((e) => e.idx === remove);
    const merge = async () => {
        if (!window.confirm(`Retirer « ${rm.nom} » (${fmt(rm.montant_ttc)}) du dossier ${p.vehicle_nom} ?\nLa ligne « ${kp.nom} » est conservée. La marge et la commission seront recalculées avec une seule dépense. Action tracée et annulable.`)) return;
        setBusy(true);
        try {
            const r = await axios.post(`${API_URL}/api/frais-doublons/merge`, { vehicle_id: p.vehicle_id, keep_idx: keep, remove_idx: remove, expected: { keep_nom: kp.nom, remove_nom: rm.nom } }, { withCredentials: true });
            toast.success('Doublon corrigé', {
                action: { label: 'Annuler', onClick: () => axios.post(`${API_URL}/api/frais-doublons/annuler/${r.data.correction_id}`, {}, { withCredentials: true }).then(() => { toast.success('Correction annulée'); onDone(); }).catch((e) => toast.error(e?.response?.data?.detail || 'Erreur')) },
                duration: 10000,
            });
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
        setBusy(false);
    };
    return (
        <div className="p-4 rounded-xl border border-gray-200 bg-white" data-testid={`doublon-${p.vehicle_id}-${p.keep_idx}-${p.remove_idx}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-sm font-semibold text-gray-900 min-w-0 break-words">{p.vehicle_nom} <span className="font-mono text-xs text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 ml-1">{p.immatriculation}</span></p>
                <span className="text-xs text-gray-500 shrink-0">{p.date_vente ? 'Vendu' : 'Actif'}</span>
            </div>
            <div className="space-y-2">
                {p.entries.map((e) => (
                    <label key={e.idx} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${keep === e.idx ? 'border-[#6366F1] bg-[#F5F5FF]' : 'border-gray-200 bg-white'}`} data-testid={`doublon-keep-${p.vehicle_id}-${e.idx}`}>
                        <input type="radio" checked={keep === e.idx} onChange={() => setKeep(e.idx)} className="accent-[#6366F1] shrink-0 self-start mt-1" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-900 break-words">{e.nom || '—'}</p>
                            <p className="text-xs text-gray-500">{e.lie_facture ? 'Liée à la facture scannée' : 'Saisie manuelle'}{e.tva_recuperable ? ' · TVA récupérable' : ''}</p>
                            <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-sm font-mono font-semibold text-gray-900">{fmt(e.montant_ttc)}</span>
                                <span className={`text-[11px] px-2 py-0.5 rounded-full ${keep === e.idx ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{keep === e.idx ? 'Conserver' : 'Retirer'}</span>
                            </div>
                        </div>
                    </label>
                ))}
            </div>
            <div className="flex justify-end mt-3">
                <Button onClick={merge} disabled={busy} className="h-9 text-xs bg-[#6366F1] hover:bg-[#4F46E5] text-white" data-testid={`doublon-merge-${p.vehicle_id}-${p.remove_idx}`}>
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}Valider la correction
                </Button>
            </div>
        </div>
    );
}

export const FraisDoublonsPanel = () => {
    const [data, setData] = useState({ items: [], count: 0 });
    const [open, setOpen] = useState(false);
    const load = useCallback(() => {
        axios.get(`${API_URL}/api/frais-doublons`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);
    if (!data.count) return null;
    return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 sm:p-5 mb-6" data-testid="doublons-panel">
            <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="doublons-toggle">
                <Copy className="w-4 h-4 text-amber-700" />
                <span className="text-sm font-semibold text-gray-900 flex-1">Doublons probables à vérifier</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="doublons-count">{data.count}</span>
                {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </button>
            {open && (
                <div className="mt-4 space-y-3">
                    <p className="text-xs text-gray-600 flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5" />Même montant et même fournisseur sur un dossier. Rien n'est corrigé sans votre validation.</p>
                    {data.items.map((p) => <Pair key={`${p.vehicle_id}-${p.keep_idx}-${p.remove_idx}`} p={p} onDone={load} />)}
                </div>
            )}
        </div>
    );
};
